import { calculatePV } from './pv/calculatePV';
import { DataService } from '../../services/data_service';
import { Document, Font, Image, Page, pdf, Text, View } from '@react-pdf/renderer';
import { fn0nZ } from '../../core/formatter/formatNumber';
import { IDs } from '../../helpers/IDs';
import { lazyInject } from '../../services/inversify';
import * as Moment from 'moment';
import { parsePdfSub2 } from '../datenauswertung/generisch/helper';
import * as React from 'react';
import { SectionStyle } from '../../helpers/SectionStyle';
import { StackedDiagram } from '../datenauswertung/helper/stackedBarChart';
import style from './ZusatztoolsPv.less';
import { svgToPng } from '../helper';
import { TriggerService } from '../../core/trigger/trigger_service';
import { WActionButton } from '../../core/elements/WActionButton';
import { WHelpIcon } from '../../core/elements/WHelpIcon';
import { WpdfText } from '../datenauswertung/pdf/WpdfText.';
import { WpdfView } from '../datenauswertung/pdf/WpdfView';
import { WSpinner } from '../../core/elements/spinner/WSpinner';
import { ZusatztoolsPvEingabe } from './ZusatztoolsPvEingabe';
import { ZusatztoolsPvTechnisch } from './ZusatztoolsPvTechnisch';
import { ZusatztoolsPvWirtschaftlich } from './ZusatztoolsPvWirtschaftlich';


export interface ZusatztoolsPvEinAusgabeProps {
    data: any;
    isExpert?: boolean;
}

export interface ZusatztoolsPvEinAusgabeState {
    isLoading: boolean;
    results: any[];
    varianten: any[];
    variante: number;
    dachausrichtung: string;
    dachflaeche: number;
    dachneigung: number;
    gesamtkosten: number;
    groesseAnlage: number;
    investitionskosten: number;
    jahrInbetriebnahme: number;
    lastprofil: string;
    monatInbetriebnahme: number;
    strompreis: number;
    strompreissteigerung: number;
    stromverbrauch: number;
    curEingabeTid: number;
    curInput: string;
    showSpinner: boolean;
}

export class ZusatztoolsPvEinAusgabe extends React.Component<ZusatztoolsPvEinAusgabeProps, ZusatztoolsPvEinAusgabeState> {

    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: false,
            results: [],
            variante: 0,
            varianten: this.varianten[this.props.isExpert ? 1 : 0] || [],
            dachausrichtung: undefined,
            dachflaeche: undefined,
            dachneigung: undefined,
            gesamtkosten: undefined,
            groesseAnlage: undefined,
            investitionskosten: undefined,
            jahrInbetriebnahme: undefined,
            lastprofil: undefined,
            monatInbetriebnahme: undefined,
            strompreis: undefined,
            strompreissteigerung: undefined,
            stromverbrauch: undefined,
            curEingabeTid: null,
            curInput: null,
            showSpinner: true
        };
        this.init = this.init.bind(this);
        Moment.locale('de');
    }

    @lazyInject(DataService) private dataService: DataService;
    @lazyInject(TriggerService) private triggerService: TriggerService;

    private backgroundColorChartBox: string;
    private calculateHandle: number = null;
    private colors: string[];
    private params: any = {};
    private varianten: any[] = [
        ['Maximaler Stromertrag', 'Maximale Rendite'],
        ['Ausgewählte PV-Leistung', 'Maximaler Stromertrag', 'Maximale Rendite', 'PV Größe \n- 5 kWp', 'PV Größe \n+ 5 kWp']
    ];

    componentDidMount(): void {
        const Section: any = new SectionStyle();
        this.backgroundColorChartBox = Section.getPropertyValue('--color-section-table-background');
        this.colors = [
            Section.getPropertyValue('--color-main-section-dateneingabe') || '#6692c3',
            Section.getPropertyValue('--color-section-main') || '#f79646',
        ];
        window.setTimeout(() => this.init(), 100);
    }

    calculate(initial?: boolean): void {
        if (initial) { // Beim 1. Mal sofort
            this.setState({ showSpinner: true }, () => this.calculateNow());
        } else {
            // Erst 2 Sekunden nach der letzten Eingabe speichern
            const handle = window.setTimeout(() => {
                if (this.calculateHandle === handle) {
                    this.setState(
                        { showSpinner: true },
                        () => window.setTimeout(() => this.calculateNow(), 100)
                    );
                }
            }, 2000);
            this.calculateHandle = handle;
        }
    }

    calculateNow(): void {
        // console.log('PV-State', this.state);
        const pv_faktor_leistung: number = +this.params.pv_faktor_leistung || 5;
        const inbetriebnahme: Date = new Date(+this.state.jahrInbetriebnahme, +this.state.monatInbetriebnahme, 1);
        const groesse: number = +this.state.groesseAnlage || 0;
        const max_groesse: number = +this.state.dachflaeche / pv_faktor_leistung;
        const results: any[] = [];

        const curInput: string = this.state.curInput || null;
        const gesamtkosten: number = this.state.gesamtkosten || undefined;
        let investitionskosten: number = this.state.investitionskosten || undefined;

        if (curInput === 'gesamtkosten') {
            investitionskosten = gesamtkosten
                ? (groesse > 0 ? gesamtkosten / groesse : undefined)
                : undefined;
        }

        this.params = {
            ...this.params,
            pv_jahr_inbetriebnahme: this.props.isExpert ? +this.state.jahrInbetriebnahme : +this.state.jahrInbetriebnahme,
            pv_monat_inbetriebnahme: this.props.isExpert ? +this.state.monatInbetriebnahme : new Date().getMonth() + 1,
            pv_strompreissteigerung: +this.state.strompreissteigerung / 100,
            pv_strompreis_erstes_jahr: +this.state.strompreis
        };
        this.saveEingabe();
        // Wir machen zunächst die Simulation für die Dachgröße in 5er-Schritten
        let resultMaxRendite = undefined;
        // console.log('PV PARAMS', this.params);
        for (let i = 5; i <= max_groesse && i <= 100; i += 5) {
            const resultSim: any = calculatePV(
                this.props.data, this.params, this.state.lastprofil, this.state.dachausrichtung, this.state.dachneigung,
                i, this.state.stromverbrauch, this.state.dachflaeche, inbetriebnahme, this.props.isExpert,
                investitionskosten
            );

            if (!resultMaxRendite) {
                resultMaxRendite = resultSim;
            } else if (resultSim.wirtschaftlich.ammortisation < resultMaxRendite.wirtschaftlich.ammortisation) {
                resultMaxRendite = resultSim;
            }
            // console.log('PV-SIM: ', i, resultSim);
        }
        const resultSimMax: any = calculatePV(
            this.props.data, this.params, this.state.lastprofil, this.state.dachausrichtung, this.state.dachneigung,
            max_groesse > 100 ? 100 : max_groesse, this.state.stromverbrauch, this.state.dachflaeche, inbetriebnahme, this.props.isExpert,
            investitionskosten
        );
        // console.log('PV-SIM-MAX: ', max_groesse, resultSimMax);
        if (!resultMaxRendite) {
            resultMaxRendite = resultSimMax;
        } else if (resultSimMax.wirtschaftlich.ammortisation < resultMaxRendite.wirtschaftlich.ammortisation) {
            resultMaxRendite = resultSimMax;
        }

        if (this.props.isExpert) {
            const resultInput: any = calculatePV(
                this.props.data, this.params, this.state.lastprofil, this.state.dachausrichtung, this.state.dachneigung,
                groesse, this.state.stromverbrauch, this.state.dachflaeche, inbetriebnahme, this.props.isExpert, investitionskosten
            );
            const wirtschaftlich: any = (resultInput || {}).wirtschaftlich || {};
            // Ausgewählte PV-Leistung
            results.push(resultInput);
            // Maximaler ERTRAG
            results.push(resultSimMax);
            // Maximale RENDITE
            results.push(resultMaxRendite);
            // PV Größe - 5 kWp
            results.push(calculatePV(
                this.props.data, this.params, this.state.lastprofil, this.state.dachausrichtung, this.state.dachneigung,
                (groesse >= 5 ? groesse - 5 : 0), this.state.stromverbrauch, this.state.dachflaeche, inbetriebnahme,
                this.props.isExpert, investitionskosten
            ));
            // PV Größe + 5 kWp
            results.push(calculatePV(
                this.props.data, this.params, this.state.lastprofil, this.state.dachausrichtung, this.state.dachneigung,
                groesse + 5, this.state.stromverbrauch, this.state.dachflaeche, inbetriebnahme, this.props.isExpert, investitionskosten
            ));
            this.setState({ results, curInput: null, showSpinner: false }, () => {
                if (curInput === 'investitionskosten') {
                    this.setState({ gesamtkosten: investitionskosten ? wirtschaftlich.investitions_kosten : undefined });
                } else if (curInput === 'gesamtkosten') {
                    this.setState({ investitionskosten });
                }
            });
        } else {
            // Maximaler ERTRAG
            results.push(calculatePV(
                this.props.data, this.params, this.state.lastprofil, this.state.dachausrichtung, this.state.dachneigung,
                max_groesse > 100 ? 100 : max_groesse, this.state.stromverbrauch, this.state.dachflaeche, inbetriebnahme,
                this.props.isExpert, investitionskosten
            ));
            // Maximale RENDITE
            results.push(resultMaxRendite);
            // console.log(results[0]);
            this.setState({ results, showSpinner: false });
        }
    }

    getBars(): any[] {
        return [
            { name: 'Netzeinspeisung PV [kWh]', color: this.colors[0] },
            { name: 'Eigenstromverbrauch [kWh]', color: this.colors[1] }
        ];
    }

    getBars2(): any[] {
        return [
            { name: null, color: this.colors[0] },
            { name: null, color: this.colors[0] }
        ];
    }

    getBars3(): any[] {
        return [
            { name: 'Netzbezug [kWh]', color: this.colors[0] },
            { name: 'Eigenstromverbrauch [kWh]', color: this.colors[1] }
        ];
    }

    getData(): any[] {
        const data: any = (this.state.results[this.state.variante] || {}).technisch || {};

        return [
            { name: null, values: [data.netzeinspeisung || 0, data.selbstverbrauch || 0] }
        ];
    }

    getData2(): any[] {
        const data: any = (this.state.results[this.state.variante] || {}).technisch || {};

        return [
            { name: 'Gesamter Stromverbrauch [kWh]', values: [data.stromverbrauch || 0] },
            { name: 'Erzeugte Energie PV [kWh]', values: [data.erzeugte_energie || 0] }
        ];
    }

    getData3(): any[] {
        const data: any = (this.state.results[this.state.variante] || {}).technisch || {};

        return (data.monate || []).map((dataItem: any, monat: number) => {
            return {
                name: Moment([Moment().year(), monat, 1]).format('MMMM'),
                values: [dataItem.netzbezug || 0, dataItem.selbstverbrauch || 0]
            };
        });
    }

    getMaxYaxisValue(value?: number): number {
        const data: any = (this.state.results[this.state.variante] || {}).technisch || {};
        const verbrauch: number = Math.round(typeof value === 'undefined'
            ? Math.max(+data.erzeugte_energie || 0, +data.stromverbrauch || 0)
            : +value);

        if (verbrauch <= 0) {
            return 0;
        }
        const exp: number = +verbrauch.toString().length - 1;
        const base: number = +Math.pow(10, exp) || 1;
        const max: number = (Math.ceil((verbrauch / base)) * base);
        const diff: number = max - verbrauch;

        if (+(diff / verbrauch) <= 0.2) {
            return max;
        } else {
            return Math.round(+diff.toString().substring(1) + verbrauch);
        }
    }

    getStromPreis(): number {
        const kennzahlen = this.props.data[IDs.KENNZIFFERN_BERECHNET];
        const jahrMax = Moment().year() - 1;
        let strom_verbrauch = undefined;
        let strom_kosten = undefined;

        for (let i = 0; i < 10; i++) {
            const jahr = jahrMax - i;

            if (
                kennzahlen && kennzahlen[jahr] && kennzahlen[jahr].strom_verbrauch_gesamt > 0
                && kennzahlen[jahr].strom_kosten_gesamt > 0
            ) {
                strom_verbrauch = kennzahlen[jahr].strom_verbrauch_gesamt;
                strom_kosten = kennzahlen[jahr].strom_kosten_gesamt;
                break;
            }
        }
        return (strom_verbrauch > 0 && strom_kosten > 0) ? Math.round((strom_kosten / strom_verbrauch) * 1000) / 1000 : undefined;
    }

    getStromVerbrauch(): number {
        const kennzahlen = this.props.data[IDs.KENNZIFFERN_BERECHNET];
        const jahrMax = Moment().year() - 1;
        let strom_verbrauch = undefined;

        for (let i = 0; i < 10; i++) {
            const jahr = jahrMax - i;

            if (kennzahlen && kennzahlen[jahr] && kennzahlen[jahr].strom_verbrauch_gesamt > 0) {
                strom_verbrauch = kennzahlen[jahr].strom_verbrauch_gesamt;
                break;
            }
        }
        return Math.round(strom_verbrauch * 1000) / 1000;
    }

    getStyle(isPdf?: boolean): any {
        const Section: any = new SectionStyle();
        const [backgroundColorSection, colorSection] = Section.getPropertyValues(['--color-section-main', '--color-button-text']);

        return ({
            main: {
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap'
            },
            header: {
                fontSize: '18pt',
                fontFamily: 'Helvetica-Bold',
                color: '#555',
                width: '100%',
                height: 85,
                top: 0,
                left: 0,
                padding: 0,
                position: 'relatitive',
                display: 'block'
            },
            headerImage: {
                width: 241,
                height: 40,
                top: 0,
                right: 0,
                position: 'absolute',
                display: 'inline-block'
            },
            headerText: {
            },
            headerSubText: {
                fontSize: '18pt',
                color: backgroundColorSection || '#5e8bc2'
            },
            headerSubText2: {
                fontSize: '11pt',
                color: '#555',
                marginTop: 10
            },
            headerSubText3: {
                fontSize: '10pt',
                color: backgroundColorSection || '#6692c2'
            },
            footer: {
                fontSize: '8pt',
                right: 20,
                bottom: 15,
                left: 20,
                width: '100%',
                height: 'auto',
                padding: 0,
                position: 'absolute',
                display: 'block',
                boxSizing: 'border-box'
            },
            footerSeiten: {
                right: 0,
                position: 'absolute'
            },
            left: {
                minWidth: 720,
                maxWidth: 720,
                margin: '0 0 0 20px',
                flexDirection: 'column'
            },
            right: {
                maxWidth: 640,
                top: 32,
                margin: '0 0 0 20px',
                position: 'sticky',
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignSelf: 'flex-start'
            },
            text: {
                fontSize: '9pt',
                maxWidth: '100%',
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                boxSizing: 'border-box'
            },
            text_vertical: {
                fontSize: '9pt',
                maxWidth: '100%',
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                boxSizing: 'border-box'
            },
            strong: {
                ...(isPdf && { fontFamily: 'Helvetica-Bold' }),
                fontSize: '10pt',
                fontWeight: 'bold'
            },
            underline: {
                textDecoration: 'underline'
            },
            trennlinie: {
                maxWidth: '100%',
                maxHeight: 0,
                marginTop: 15,
                borderTop: '2pt solid #888',
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                boxSizing: 'border-box'
            },
            ueberschrift: {
                ...(isPdf && { fontFamily: 'Helvetica-Bold' }),
                fontSize: '10pt',
                fontWeight: 'bold',
                color: colorSection || '#555',
                backgroundColor: backgroundColorSection || '#bccae3',
                maxWidth: 720,
                marginTop: 15,
                padding: isPdf ? 2 : 4,
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                boxSizing: 'border-box',
                justifyContent: 'space-between'
            },
            diagramm: {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                alignItems: 'flex-start',
                boxSizing: 'border-box'
            },
            diagramm_image: {
                borderRight: '1 solid #ddd',
                borderLeft: '1 solid #ddd',
                borderBottom: '1 solid #ddd'
            },
            diagramm_ueberschrift: {
                ...(isPdf && { fontFamily: 'Helvetica-Bold' }),
                fontSize: '11pt',
                fontWeight: 'bold',
                textAlign: 'center',
                color: '#333',
                width: '100%',
                padding: 4,
                borderTop: '1 solid #ddd',
                borderRight: '1 solid #ddd',
                borderLeft: '1 solid #ddd',
                boxSizing: 'border-box'
            },
            legend: {
                marginRight: 2,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
            },
            legend_icon: {
                width: 6,
                height: 6,
                marginRight: 4,
                marginLeft: 4
            },
            legend_text: {
                fontSize: '10pt',
                color: '#666',
                marginRight: 4,
                display: 'flex',
                flexDirection: 'row'
            },
            legend_wrapper: {
                marginBottom: 10,
                marginLeft: 15,
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'flex-start'
            },
            actions: {
                backgroundColor: '#eee',
                maxWidth: 1380,
                top: 0,
                margin: '0 20px',
                position: 'sticky',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                boxSizing: 'border-box'
            },
            hinweis: {
                fontSize: '9pt',
                maxWidth: '100%',
                marginTop: 15,
                ...(!isPdf
                    ? {
                        margin: '10px 20px 0 20px',
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'row',
                        alignItems: 'baseline'
                    }
                    : {
                        marginTop: 10
                    }
                ),
                boxSizing: 'border-box'
            },
            pdf: {
                fontFamily: 'Calibri',
                fontSize: '9pt'
            },
            page: {
                padding: '20px 20px 20mm 20px'
            }
        });
    }

    getValue(value: any): any {
        return typeof value !== 'undefined' && value !== null && value !== '' && !isNaN(+value) ? +value : null;
    }

    init(): void {
        const data: any[] = this.props.data[IDs.ENT_PV_EINGABE] || [];
        const jahr: number = new Date().getFullYear();
        const monat: number = new Date().getMonth() + 1;
        const modus: any[] = [
            data.find((eingabe1: any) => +eingabe1.modus === 0) || {}, // Standard
            data.find((eingabe2: any) => +eingabe2.modus === 1) || {} // Experte
        ];
        const eingabe: any = modus[this.props.isExpert ? 1 : 0] || {};
        const systemparameter: any = {};

        (this.props.data[IDs.ENT_SYSTEMPARAMETER] || []).forEach((element: any) => {
            const { parameter, wert } = element;
            let value: any = wert;

            if (!this.props.isExpert) {
                switch (parameter) {
                    case 'pv_jahr_inbetriebnahme': // muss später aktiviert werden
                        // value = jahr;
                        break;
                    case 'pv_monat_inbetriebnahme':
                        value = monat;
                        break;
                    case 'pv_strompreissteigerung':
                        value = +wert / 100;
                        break;
                }
            }
            systemparameter[parameter] = value;
        });
        this.params = systemparameter;
        this.setState(
            {
                curEingabeTid: eingabe.tid || 0,
                stromverbrauch: eingabe.stromverbrauch || this.getStromVerbrauch(),
                dachflaeche: eingabe.dachflaeche/* || 100*/,
                dachneigung: eingabe.dachneigung/* || 30*/,
                dachausrichtung: eingabe.dachausrichtung/* || 'ost'*/,
                lastprofil: eingabe.lastprofil/* || 'g0'*/,
                strompreis: eingabe.strompreis || this.getStromPreis(),
                groesseAnlage: eingabe.anlagengroesse || 30,
                strompreissteigerung: eingabe.strompreissteigerung || undefined,
                jahrInbetriebnahme: eingabe.jahr_inbetriebnahme || undefined,
                // muss später aktiviert werden
                // jahrInbetriebnahme: this.props.isExpert ? (eingabe.jahr_inbetriebnahme || undefined) : jahr,
                monatInbetriebnahme: this.props.isExpert ? (eingabe.monat_inbetriebnahme || undefined) : monat,
                investitionskosten: eingabe.investitionskosten || undefined,
                gesamtkosten: eingabe.gesamtkosten || undefined
            },
            () => this.calculate(true)
        );
    }

    async processPdf(pdfData: JSX.Element): Promise<void> {
        try {
            const blob = await pdf(pdfData).toBlob();
            const pdfString: string = URL.createObjectURL(blob);
            this.setState({ isLoading: false });
            const variante: string = (this.state.varianten[this.state.variante] || '')
                .split('\n').map((substring: string) => substring.trim()).join(' ');
            const filename: string = (
                `PV Variante ${(+this.state.variante || 0) + 1} - ${variante} ` +
                `[${(new Date()).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' })}]` +
                '.pdf'
            );
            // const a = document.createElement('a');
            // a.target = '_blank';
            // a.title = filename;
            // a.href = pdfString;
            // a.download = filename;
            // document.body.appendChild(a);
            // a.click();
            // document.body.removeChild(a);
            saveAs(pdfString, filename);
        } catch (error) {
            // console.log('error: ', error);
        }
    }

    async processPdfData(): Promise<void> {
        const charts: any[] = await [];
        const data: any[] = await [];
        const recharts: NodeListOf<HTMLElement> = await document.querySelectorAll('.recharts-responsive-container');
        const styles: any = this.getStyle(true);

        for (let index = 0, length: number = recharts.length; index < length; ++index) {
            const chart: HTMLElement = recharts[index].querySelector('.recharts-surface');
            const id: string = recharts[index].id || null;
            const legend: NodeListOf<HTMLElement> = recharts[index].querySelectorAll('.recharts-legend-wrapper li');
            const legendItems: any[] = Array.from(legend)
                .map((li: any) => {
                    const color = `#${
                        ((li.firstChild.style.backgroundColor || '').substr(4).split(')')[0].split(', '))
                            .map((rgb: number) => (+rgb).toString(16))
                            .join('')
                    }`;
                    return { data: li.lastChild.data, color };
                });

            if (!chart) {
                continue;
            }
            charts.push({
                ...(id && { id }),
                html: chart,
                legend: legendItems || []
            });
        }

        if (!charts.length) {
            this.renderPdf();
        }

        charts.forEach(async (item: any): Promise<void> => {
            const html: HTMLElement = item.html || <svg />;
            html.style.fontFamily = styles.pdf.fontFamily || null;
            html.style.fontSize = styles.pdf.fontSize || null;
            const id: string = item.id || null;
            const legend: any = item.legend || [];
            const rect = html.getBoundingClientRect();
            const svg = new XMLSerializer().serializeToString(html);
            const svgBlob = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
            const image: any = await svgToPng(svgBlob, rect.width, rect.height, 3);
            data.push({
                ...(id ? { id } : {}),
                image: this.renderChart({
                    data: image,
                    height: rect.height,
                    width: rect.width
                }),
                legend: legend ? this.renderPdfLegend(legend) : null
            });

            if (data.length === charts.length) {
                this.renderPdf(data);
            }
        });
    }

    render(): JSX.Element {
        const styles: any = this.getStyle();

        if (!this.props.data) {
            return <div />;
        }

        if (!this.state.results || this.state.results.length === 0) {
            return <WSpinner />;
        }
        const maxYaxisValue: number = this.getMaxYaxisValue();

        return (
            <>
                <div style={styles.actions}>
                    {this.renderPdfButton()}
                    {this.renderAuswahlVariante()}
                </div>
                <div className={style.wrapper} style={styles.main}>
                    <div style={styles.left}>
                        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', boxSizing: 'border-box' }}>
                            {this.renderText()}
                            <ZusatztoolsPvEingabe
                                data={this.props.data}
                                isExpert={this.props.isExpert}
                                dachausrichtung={this.state.dachausrichtung}
                                dachflaeche={this.state.dachflaeche}
                                dachneigung={this.state.dachneigung}
                                gesamtkosten={this.state.gesamtkosten}
                                groesseAnlage={this.state.groesseAnlage}
                                investitionskosten={this.state.investitionskosten}
                                jahrInbetriebnahme={this.state.jahrInbetriebnahme}
                                lastprofil={this.state.lastprofil}
                                monatInbetriebnahme={this.state.monatInbetriebnahme}
                                strompreis={this.state.strompreis}
                                strompreissteigerung={this.state.strompreissteigerung}
                                stromverbrauch={this.state.stromverbrauch}
                                onDachausrichtungChange={(data: string) => this.setState({ dachausrichtung: data }, this.calculate)}
                                onDachflaecheChange={(data: number) => this.setState({ dachflaeche: data }, this.calculate)}
                                onDachneigungChange={(data: number) => this.setState({ dachneigung: data }, this.calculate)}
                                onGesamtkostenChange={(data: number) => {
                                    this.setState({ gesamtkosten: data, curInput: 'gesamtkosten' }, this.calculate);
                                }}
                                onGroesseAnlageChange={(data: number) => this.setState({ groesseAnlage: data }, this.calculate)}
                                onInvestitionskostenChange={(data: number) => {
                                    this.setState({ investitionskosten: data, curInput: 'investitionskosten' }, this.calculate);
                                }}
                                onJahrInbetriebnahmeChange={(data: number) => this.setState({ jahrInbetriebnahme: data }, this.calculate)}
                                onLastprofilChange={(data: string) => this.setState({ lastprofil: data }, this.calculate)}
                                onMonatInbetriebnahmeChange={(data: number) => this.setState({ monatInbetriebnahme: data }, this.calculate)}
                                onStrompreisChange={(data: number) => this.setState({ strompreis: data }, this.calculate)}
                                onStrompreissteigerungChange={(data: number) => {
                                    this.setState({ strompreissteigerung: data }, this.calculate);
                                }}
                                onStromverbrauchChange={(data: number) => this.setState({ stromverbrauch: data }, this.calculate)}
                                onReloadStromPreis={() => this.setState({ strompreis: this.getStromPreis() }, this.calculate)}
                                onReloadStromVerbrauch={() => this.setState({ stromverbrauch: this.getStromVerbrauch() }, this.calculate)}
                            />
                        </div>
                        {this.renderTrennlinie()}
                        {this.renderUeberschrift()}
                        <ZusatztoolsPvTechnisch
                            data={this.props.data}
                            varianten={this.state.varianten}
                            variante={this.state.variante}
                            isExpert={this.props.isExpert}
                            results={this.state.results}
                        />
                        <ZusatztoolsPvWirtschaftlich
                            data={this.props.data}
                            varianten={this.state.varianten}
                            variante={this.state.variante}
                            isExpert={this.props.isExpert}
                            results={this.state.results}
                        />
                    </div>
                    <div style={styles.right}>
                        <div style={{
                            display: 'flex', flex: 1, flexDirection: 'column', marginTop: 32, marginRight: 15, minWidth: 262.5,
                            maxWidth: 262.5, minHeight: 293, maxHeight: 293
                        }}>
                            <div
                                style={{
                                    backgroundColor: this.backgroundColorChartBox, height: 30, padding: 5, border: '1px solid #eee',
                                    boxSizing: 'border-box', display: 'flex', flex: 1, flexDirection: 'column'
                                }}
                            >
                                <div style={styles.diagramm_ueberschrift}>PV Erzeugung</div>
                                <StackedDiagram
                                    data={this.getData()}
                                    id='pv_erzeugung'
                                    bars={this.getBars()}
                                    yAxisProps={{ domain: [0, maxYaxisValue], axisLine: false, tickLine: false }}
                                    legendBottom
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex', flex: 1, flexDirection: 'column', marginTop: 32, minWidth: 362.5, maxWidth: 362.5,
                                minHeight: 293, maxHeight: 293
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: this.backgroundColorChartBox, height: 30, padding: 5, border: '1px solid #eee',
                                    boxSizing: 'border-box', display: 'flex', flex: 1, flexDirection: 'column'
                                }}
                            >
                                <div style={styles.diagramm_ueberschrift}>Stromverbrauch vs. Stromerzeugung</div>
                                <StackedDiagram
                                    data={this.getData2()}
                                    id='stromverbrauch_stromerzeugung'
                                    bars={this.getBars2()}
                                    xAxisProps={{ height: 40 }}
                                    yAxisProps={{ domain: [0, maxYaxisValue], axisLine: false, tickLine: false }}
                                    disableLegend
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex', flex: 1, flexDirection: 'column', marginTop: 16, minWidth: 640, maxWidth: 640,
                                minHeight: 429, maxHeight: 429
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: this.backgroundColorChartBox, height: 30, padding: 5, border: '1px solid #eee',
                                    boxSizing: 'border-box', display: 'flex', flex: 1, flexDirection: 'column'
                                }}
                            >
                                <div style={styles.diagramm_ueberschrift}>Netzbezug vs. Eigenstrom</div>
                                <StackedDiagram
                                    data={this.getData3()}
                                    id='netzbezug_eigenstrom'
                                    bars={this.getBars3()}
                                    legendBottom
                                    xAxisProps={{ height: 40, angle: -45, style: { fontSize: '7pt' } }}
                                    yAxisProps={{ axisLine: false, tickLine: false }}
                                    barProps={{ barSize: 16 }}
                                />
                            </div>
                        </div>
                    </div>
                    {this.state.isLoading && <WSpinner label='PDF...' />}
                </div>
                {this.state.showSpinner && <WSpinner />}
                {this.renderHinweis()}
            </>
        );
    }

    renderAuswahlVariante(): JSX.Element {
        const options: JSX.Element[] = [];
        const varianten: any[] = this.varianten[this.props.isExpert ? 1 : 0] || [];

        varianten.forEach((variante: string, index: number) => {
            options.push(
                <option key={`variante-${variante}`} value={index}>{variante}</option>
            );
        });

        return (
            <div>
                <label
                    style={{ marginLeft: 5, cursor: 'pointer' }}
                    htmlFor='auswahl-variante'
                >
                    Grafische Darstellung für Variante:
                </label>
                <select
                    id='auswahl-variante'
                    style={{ margin: '0 5px', cursor: 'pointer' }}
                    value={this.state.variante}
                    onChange={(event: any) => this.setState({ variante: event.target.value || 0 })}
                >
                    {options}
                </select>
            </div>
        );
    }

    renderChart(data: any): JSX.Element {
        return <Image src={data.data} style={{ maxWidth: data.width }} />;
    }

    renderFooter(): JSX.Element {
        const styles: any = this.getStyle(true);

        return (
            <View style={styles.footer} fixed>
                <Text>{Moment().format('DD.MM.YYYY')}</Text>
                <View style={styles.footerSeiten}>
                    <Text
                        render={({ pageNumber, totalPages }) => {
                            if (totalPages > 1) {
                                return `${pageNumber}/${totalPages}`;
                            }
                        }}
                        fixed
                    />
                </View>
            </View>
        );
    }

    renderHeader(): JSX.Element {
        const header = require('../../img/logo_mittel2.png');
        const variante: string = (this.state.varianten[this.state.variante] || '')
            .split('\n').map((substring: string) => substring.trim()).join(' ');
        const styles: any = this.getStyle(true);

        return (
            <View style={styles.header} fixed>
                <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline' }}>
                    <Text style={styles.headerText}>E-Tool</Text>
                </View>
                <View style={{ ...styles.headerSubText2, display: 'flex', flexDirection: 'row', alignItems: 'baseline' }}>
                    <Text>PV Berechnung:&nbsp;</Text>
                    <Text style={{ ...styles.headerSubText3, margin: 0 }}>
                        {parsePdfSub2(variante, styles.headerSubText3)}
                    </Text>
                </View>
                <Image src={header} style={styles.headerImage} />
            </View>
        );
    }

    renderHinweis(isPdf?: boolean): JSX.Element {
        const styles: any = this.getStyle(isPdf);

        return (
            <WpdfView style={styles.hinweis} pdf={isPdf}>
                <WpdfText style={styles.strong} pdf={isPdf}>Hinweis:&nbsp;</WpdfText>
                <WpdfText pdf={isPdf}>
                    Die Vergütungssätze beziehen auf den Tag der Hochrechnung.
                    Die hier dargestellten Berechnungen und Diagramme wurden nach bestem Wissen und Gewissen erstellt; wir übernehmen aber
                    keine Haftung / Gewähr für etwaige Berechnungsfehler und deren Folgen.
                </WpdfText>
            </WpdfView>
        );
    }

    async renderPdf(charts: any[] = []): Promise<void> {
        const fontCalibri = require('../../font/calibri_regular.ttf');
        const netzbezug_eigenstrom: any = charts.find((item: any) => (item || {}).id === 'netzbezug_eigenstrom') || {};
        const pv_erzeugung: any = charts.find((item: any) => (item || {}).id === 'pv_erzeugung') || {};
        const stromverbrauch_stromerzeugung: any = charts.find((item: any) => (item || {}).id === 'stromverbrauch_stromerzeugung') || {};
        Font.register({ family: 'Calibri', src: fontCalibri });
        const styles: any = this.getStyle(true);
        this.processPdf(
            <Document>
                <Page size='A4' orientation='portrait' style={styles.page}>
                    {this.renderHeader()}
                    {this.renderText(true)}
                    <View style={{ display: 'flex', flexDirection: 'column' }}>
                        <View style={{ display: 'flex', flexDirection: 'column' }}>
                            <ZusatztoolsPvEingabe
                                data={this.props.data}
                                isExpert={this.props.isExpert}
                                isPdf
                                dachausrichtung={this.state.dachausrichtung}
                                dachflaeche={this.state.dachflaeche}
                                dachneigung={this.state.dachneigung}
                                gesamtkosten={this.state.gesamtkosten}
                                groesseAnlage={this.state.groesseAnlage}
                                investitionskosten={this.state.investitionskosten}
                                jahrInbetriebnahme={this.state.jahrInbetriebnahme}
                                lastprofil={this.state.lastprofil}
                                monatInbetriebnahme={this.state.monatInbetriebnahme}
                                strompreis={this.state.strompreis}
                                strompreissteigerung={this.state.strompreissteigerung}
                                stromverbrauch={this.state.stromverbrauch}
                                onDachausrichtungChange={(data: string) => this.setState({ dachausrichtung: data }, this.calculate)}
                                onDachflaecheChange={(data: number) => this.setState({ dachflaeche: data }, this.calculate)}
                                onDachneigungChange={(data: number) => this.setState({ dachneigung: data }, this.calculate)}
                                onGesamtkostenChange={(data: number) => this.setState({ gesamtkosten: data })}
                                onGroesseAnlageChange={(data: number) => this.setState({ groesseAnlage: data }, this.calculate)}
                                onInvestitionskostenChange={(data: number) => this.setState({ investitionskosten: data })}
                                onJahrInbetriebnahmeChange={(data: number) => this.setState({ jahrInbetriebnahme: data })}
                                onLastprofilChange={(data: string) => this.setState({ lastprofil: data }, this.calculate)}
                                onMonatInbetriebnahmeChange={(data: number) => this.setState({ monatInbetriebnahme: data })}
                                onStrompreisChange={(data: number) => this.setState({ strompreis: data })}
                                onStrompreissteigerungChange={(data: number) => this.setState({ strompreissteigerung: data })}
                                onStromverbrauchChange={(data: number) => this.setState({ stromverbrauch: data }, this.calculate)}
                                onReloadStromPreis={() => this.setState({ strompreis: this.getStromPreis() }, this.calculate)}
                                onReloadStromVerbrauch={() => this.setState({ stromverbrauch: this.getStromVerbrauch() }, this.calculate)}

                            />
                            {this.renderTrennlinie(true)}
                            {this.renderUeberschrift(true)}
                            <ZusatztoolsPvTechnisch
                                data={this.props.data}
                                varianten={this.state.varianten}
                                variante={this.state.variante}
                                isExpert={this.props.isExpert}
                                isPdf
                                results={this.state.results}
                            />
                            <ZusatztoolsPvWirtschaftlich
                                data={this.props.data}
                                varianten={this.state.varianten}
                                variante={this.state.variante}
                                isExpert={this.props.isExpert}
                                isPdf
                                results={this.state.results}
                            />
                        </View>
                        <View
                            style={{
                                minHeight: 225, maxHeight: 225, display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly',
                                marginTop: 15
                            }}
                        >
                            {Object.keys(pv_erzeugung).length > 0 &&
                                this.renderPdfChart(
                                    { ...pv_erzeugung, titel: 'PV Erzeugung' },
                                    { diagramm: { maxWidth: 221 }, legend: { marginTop: -36 } }
                                )
                            }
                            {Object.keys(stromverbrauch_stromerzeugung).length > 0 &&
                                this.renderPdfChart(
                                    { ...stromverbrauch_stromerzeugung, titel: 'Stromverbrauch vs. Stromerzeugung' },
                                    { diagramm: { marginLeft: 15 } }
                                )
                            }
                        </View>
                        {Object.keys(netzbezug_eigenstrom).length > 0 &&
                            <View
                                style={{
                                    minHeight: 369, maxHeight: 369, display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly',
                                    marginTop: 35
                                }}
                            >
                                {this.renderPdfChart(
                                    { ...netzbezug_eigenstrom, titel: 'Netzbezug vs. Eigenstrom' },
                                    { diagramm: { maxWidth: '100%' }, legend: { marginTop: -26 } }
                                )}
                            </View>
                        }
                    </View>
                    {this.renderHinweis(true)}
                    {this.renderFooter()}
                </Page>
            </Document>
        );
    }

    renderPdfButton(): JSX.Element {
        return (
            <WActionButton
                label='PDF generieren'
                insideText='PDF generieren'
                key='pdf'
                action='pdf'
                onAction={() => this.setState({ isLoading: true }, this.processPdfData)}
            />
        );
    }

    renderPdfChart(data: any, styleUse?: any): JSX.Element {
        const styles: any = this.getStyle(true);
        styles.diagramm = { ...styles.diagramm, ...((styleUse || {}).diagramm || {}) };
        styles.diagramm_image = { ...styles.diagramm_image, ...(styles.diagramm.maxWidth || {}) };

        return (
            <View style={styles.diagramm} wrap={false}>
                <View style={styles.diagramm_ueberschrift}>
                    <Text>{data.titel || null}</Text>
                </View>
                <View style={styles.diagramm_image}>{data.image}</View>
                <View style={{ ...styles.legend_wrapper, ...((styleUse || {}).legend || {}) }}>
                    {data.legend}
                </View>
            </View>
        );
    }

    renderPdfLegend(data: any) {
        const styles: any = this.getStyle(true);

        return data.map((item: any) =>
            <View style={styles.legend}>
                {item.color && <Text style={{ ...styles.legend_icon, backgroundColor: item.color }}></Text>}
                {item.data && <Text style={styles.legend_text}>{item.data}</Text>}
            </View>
        );
    }

    renderText(isPdf?: boolean): JSX.Element {
        const styles: any = this.getStyle(isPdf);
        const kennzahlen = this.props.data[IDs.KENNZIFFERN_BERECHNET];
        const jahrMax = Moment().year() - 1;
        let strom_pv_einsparung = 0;

        for (let i = 0; i < 10; i++) {
            const jahr = jahrMax - i;

            if (kennzahlen && kennzahlen[jahr] && kennzahlen[jahr].strom_kosten_gesamt > 0) {
                strom_pv_einsparung = kennzahlen[jahr].strom_kosten_gesamt * 0.38;
                break;
            }
        }
        const amount: number = Math.round(strom_pv_einsparung / 100) * 100;

        return (
            <WpdfView style={{ ...(!isPdf ? { marginTop: 15 } : {}) }} pdf={isPdf}>
                <WpdfView style={styles.text} pdf={isPdf}>
                    <WpdfText style={{ ...styles.strong, ...styles.underline }} pdf={isPdf}>
                        PV-Anlage für den eigenen Betrieb
                    </WpdfText>
                </WpdfView>
                <WpdfView style={styles.text_vertical} pdf={isPdf}>
                    <WpdfText pdf={isPdf}>
                        Unter Berücksichtigung Ihres jährlichen Stromverbrauchs und unseren Erfahrungen bei installierten Anlagen von
                        Handwerksbetrieben, können Sie mit einer eigenen PV-Anlage ca. {fn0nZ(amount)} € im Jahr an
                        Stromkosten sparen.
                        Dies ist eine vergleichsweise grobe Abschätzung, welche Sie durch die Eingabe der folgenden Daten präzisieren
                        können. Als Ergebnis
                    </WpdfText>
                    <WpdfText pdf={isPdf}>
                        erhalten Sie dann eine detaillierte Übersicht mit technischen und wirtschaftlichen
                        Ergebnissen für mehrere Varianten.
                    </WpdfText>
                    <WpdfText pdf={isPdf} style={{ ...styles.strong, fontSize: '9pt' }}>
                        Weitere Hinweise/Erläuterungen finden Sie im Info-Feld "Ergebnis"
                    </WpdfText>
                </WpdfView>
            </WpdfView>
        );
    }

    renderTrennlinie(isPdf?: boolean): JSX.Element {
        const styles: any = this.getStyle(isPdf);

        return <WpdfView style={styles.trennlinie} pdf={isPdf} />;
    }

    renderUeberschrift(isPdf?: boolean): JSX.Element {
        const styles: any = this.getStyle(isPdf);

        return (
            <WpdfView style={styles.ueberschrift} pdf={isPdf}>
                <WpdfText pdf={isPdf}>Ergebnis</WpdfText>
                <WpdfText pdf={isPdf}>
                    {!isPdf &&
                        <WHelpIcon
                            data={this.props.data}
                            styleDivProps={{ display: 'flex', alignSelf: 'center' }}
                            styleProps={{ margin: 0 }}
                            label='PV-Tool Ergebnis'
                            helpKey={`pvToolErgebnis${this.props.isExpert ? '_experte' : ''}`}
                            type='theme_info'
                            style='theme_small'
                            editBar
                            useTheme
                            useWhite
                        />
                    }
                </WpdfText>
            </WpdfView>
        );
    }

    saveAlternativesSystem(): void {
        const entity: string = IDs.ENT_ALTERNATIVES_SYSTEM_TABELLE3;
        const tid: number = ((this.props.data[entity] || [])
            .find((item: any) => (item.investition_massnahme || null) === 'PV-Anlage') || {}).tid || 0;
        const result: any = ((this.state.results || [])[1] || {}).wirtschaftlich || {}; // {1} Maximale Rendite

        if (
            this.props.isExpert
            || (
                !this.props.isExpert
                && (
                    !+this.state.stromverbrauch || !+this.state.dachflaeche || !this.state.dachneigung
                    || !this.state.dachausrichtung || !this.state.lastprofil
                )
            )
        ) {
            if (!this.props.isExpert && tid) {
                this.dataService.delData(entity, IDs.MOD_ETOOL, { tid }, {}, () => this.triggerService.fire('reload', entity));
            }
            return;
        }
        this.dataService.saveData(
            entity,
            {
                tid,
                investition_massnahme: 'PV-Anlage',
                investitionssumme: typeof result.investitions_kosten === 'number' ? +result.investitions_kosten.toFixed(2) : null,
                // einsparung: null,
                // technische_nutzung: null,
                rentabilitaet_der_investition: typeof result.interner_zinsfuss === 'number'
                    ? +(result.interner_zinsfuss * 100).toFixed(2)
                    : null,
                kapitalrueckfluss: typeof result.ammortisation === 'number' ? result.ammortisation : null
            },
            null, false,
            () => this.triggerService.fire('reload', entity),
            IDs.MOD_ETOOL
        );
    }

    saveEingabe(): void {
        const dachausrichtung: string = this.state.dachausrichtung || null;
        const entity: string = IDs.ENT_PV_EINGABE;
        let dachneigung: number = this.getValue(this.state.dachneigung);

        if (dachausrichtung === 'ost_west' && [0, 90].indexOf(dachneigung) !== -1) {
            dachneigung = null;
        }
        this.dataService.saveData(
            entity,
            {
                tid: this.state.curEingabeTid || 0,
                modus: this.props.isExpert ? '1' : '0',
                stromverbrauch: this.state.stromverbrauch || null,
                dachflaeche: this.state.dachflaeche || null,
                dachneigung,
                dachausrichtung,
                lastprofil: this.state.lastprofil || null,
                strompreis: this.state.strompreis || null,
                anlagengroesse: this.state.groesseAnlage || null,
                strompreissteigerung: this.state.strompreissteigerung || null,
                jahr_inbetriebnahme: this.state.jahrInbetriebnahme || null,
                monat_inbetriebnahme: this.state.monatInbetriebnahme || null,
                investitionskosten: this.state.investitionskosten || null,
                gesamtkosten: this.state.gesamtkosten || null
            },
            null, false,
            (success: boolean, tid: number) => {
                // console.log('saveEingabe', success, 'TID:', tid);
                this.setState(
                    { curEingabeTid: this.state.curEingabeTid || tid },
                    () => {
                        this.triggerService.fire('reload', entity);
                        this.saveAlternativesSystem();
                    }
                );
            },
            IDs.MOD_ETOOL
        );
    }
}
